import { uiSurface } from "shared/ui/uiSurface"
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid"
import { Chip } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import type { AddressListItem } from "../../apis/appApi"

interface AddressesGridProps {
  rows: AddressListItem[]
  total: number
  page: number
  pageSize: number
  loading: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onSortModelChange: (model: GridSortModel) => void
  onRowClick: (id: number) => void
}

export const AddressesGrid: FC<AddressesGridProps> = ({
  rows,
  total,
  page,
  pageSize,
  loading,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
  onRowClick,
}) => {
  const { t } = useTranslation()

  const columns: GridColDef<AddressListItem>[] = [
    {
      field: "streetName",
      headerName: t("addresses.street"),
      flex: 1.5,
      minWidth: 180,
      valueGetter: (_value, row) =>
        [row.streetNumber, row.streetName].filter(Boolean).join(" "),
    },
    {
      field: "city",
      headerName: t("addresses.city"),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "state",
      headerName: t("addresses.state"),
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "postcode",
      headerName: t("addresses.postcode"),
      flex: 0.7,
      minWidth: 90,
    },
    {
      field: "country",
      headerName: t("addresses.country"),
      flex: 0.7,
      minWidth: 80,
    },
    {
      field: "clientName",
      headerName: t("addresses.client"),
      flex: 1.2,
      minWidth: 150,
      sortable: true,
      valueGetter: (_value, row) => {
        if (row.clientOrganisation) return row.clientOrganisation
        if (row.clientFirstName || row.clientLastName) {
          return [row.clientFirstName, row.clientLastName]
            .filter(Boolean)
            .join(" ")
        }
        return null
      },
      renderCell: (params) =>
        params.value ? (
          params.value
        ) : (
          <Chip
            label={t("addresses.noClient")}
            size="small"
            variant="outlined"
            color="default"
          />
        ),
    },
    {
      field: "dateCreated",
      headerName: t("addresses.dateCreated"),
      flex: 0.9,
      minWidth: 110,
      valueFormatter: (value: string | null) =>
        value ? new Date(value).toLocaleDateString() : "\u2014",
    },
  ]

  return (
    <DataGrid
      {...uiSurface("UI-DLV2-151")}
      rows={rows}
      columns={columns}
      rowCount={total}
      loading={loading}
      paginationMode="server"
      paginationModel={{ page, pageSize }}
      onPaginationModelChange={(model) => {
        onPageChange(model.page)
        onPageSizeChange(model.pageSize)
      }}
      sortingMode="server"
      onSortModelChange={onSortModelChange}
      pageSizeOptions={[10, 25, 50]}
      onRowClick={(params) => onRowClick(params.row.id)}
      disableRowSelectionOnClick
      sx={{
        "& .MuiDataGrid-row": { cursor: "pointer" },
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
      }}
      autoHeight
    />
  )
}
